import { useMsal } from "@azure/msal-react";
import {
    ChoiceGroup,
    DatePicker, defaultDatePickerStrings,
    DayOfWeek,
    DefaultButton, DirectionalHint, IColumn, IPanelProps, IRenderFunction, MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton, Separator,
    Stack,
    TextField,
    PivotItem,
    IStackProps,
    useTheme,
    Text
} from "@fluentui/react";
import { RootState } from "app/store";
import { IGridItem } from "components/controls/CustomDetailList/IGridItem";
import LookupControlBase from "components/controls/LookupControlBase";
import LookupControlDialog, { TSearchPredicateAsync } from "components/controls/LookupControlDialog";
import { appStyles, createNonStandard, createNonStandardColumns, createNonStandardTemplate, createNonStandardTemplateColumns, emitCustomEvent, EViewType, fadeIn, friendlyValue, panelButtonsStyles, reconcileTiers } from "global/Application";
import { useApplication } from "global/Application/Context";
import { MsalTokenSource } from "global/DataSources/MsalTokenSource";
import PageLoading from "global/PageLoading";
import { addUpdateNonStandards, addUpdateNonStandardTemplates } from "global/Reducer/dataSlice";
import { ArrayToLookupByPredicate, ArrayToLookupByPredicateWithMap, formatDate, parseDate, timestamp } from "global/Utility/TSFunctions";
import * as IXrmTypes from "model/CRM";
import { IPanelEvent, IValueChoiceGroupOption } from "model/Extension";
import * as FAM from "model/FlexibleAttributeModel";
import { useEffect, useMemo, useState } from "react";
import { connect, ConnectedProps, useDispatch } from "react-redux";
import { ITierLevelChange } from "views/TierWizard";
import { AppDataSource } from "../../global/DataSources/AppDataSource";
import SnapshotLookup from "./Controls/SnapshotLookup";
import InformationCallout from "components/controls/InformationCallout";
import FlexibleFieldView, { EScopeType } from "./FlexibleFieldView";
import NonStandardEditor, { EVariant } from "./NonStandardEditor";
import DatePickerControl from "../../components/controls/DatePickerControl";
import TextFieldControl from "../../components/controls/TextFieldControl";
import { ConfirmationDialog } from "../../components/controls/ConfirmationDialog";
import NonStandardTemplateEditor from "./NonStandardTemplateEditor";
import ControlMask from "../../components/controls/ControlMask";
import ToggleControl from "../../components/controls/ToggleControl";
import { EControlType } from "../../components/ControlWrapper";
import { setProcessing } from "../../global/Reducer/statusSlice";
import saveAs from "file-saver";
import NonStandardNonGridView from "../Comparison/NonStandardNonGridView";
import { WithRecycle } from "../../global/Application/dataActions";
import { useHistory } from "react-router";

interface IConfirm {
}


interface IProps extends TComponentConnectedProps {
    //TODO: unmount component with delay for animation
    isOpen: boolean;

    //nonStandardTemplate: FAM.INonStandardTemplate;

    benefit?: FAM.IBenefit;
    groupConfiguration?: FAM.IGroupConfigurationData;

    nonStandardTemplate: FAM.INonStandardTemplate | null;


    onDismiss: () => void
}

enum ENonstandardScope {
    GroupConfiguration,
    Benefit
}

enum ENonstandardTemplateScope {
    GroupConfiguration,
}


interface IScopeSearchResult extends FAM.IObjectWithNameId {
    groupConfiguration: FAM.IGroupConfigurationData | null;
    benefit: FAM.IBenefit | null;
    templateRelationShip: FAM.INonStandardTemplate | null;
}


interface INonstandardTemplateSearchResult extends FAM.IObjectWithNameId {
    item: IGroupConfigurationNonstandardTemplateScope;
}

interface INonstandardScope {
    nonStandard: FAM.INonStandard;
    parent: FAM.IBenefit | FAM.IGroupConfigurationData;
    type: ENonstandardScope;
}

interface INonstandardTemplateRelationshipScope {
    relationship: FAM.ITemplateRelationship;
    parent: FAM.IGroupConfigurationData;
    type: ENonstandardTemplateScope;
}

interface INonstandardSearchResult extends FAM.IObjectWithNameId {
    item: FAM.INonStandard;
    benefit: FAM.IBenefit | undefined;
}

interface INonstandardScope {
    nonStandard: FAM.INonStandard;
    parent: FAM.IBenefit | FAM.IGroupConfigurationData;
    type: ENonstandardScope;
}
interface IBenefitNonstandardScope extends INonstandardScope {
    parent: FAM.IBenefit
    type: ENonstandardScope.Benefit;
}
interface IGroupConfigurationNonstandardScope extends INonstandardScope {
    parent: FAM.IGroupConfigurationData;
    type: ENonstandardScope.GroupConfiguration;
}
interface IGroupConfigurationNonstandardTemplateScope extends INonstandardTemplateRelationshipScope {
    parent: FAM.IGroupConfigurationData;
    type: ENonstandardTemplateScope.GroupConfiguration;
}

interface IImportNonstandardContext {
    snapshot?: IXrmTypes.bcbs_groupconfigurationsnapshot | null;
    json?: FAM.IGroupConfigurationData | null;
    nonStandardSearchResult?: INonstandardScope[] | null;
    nonStandardTemplateRelationshipSearchResult?: INonstandardTemplateRelationshipScope[] | null;
    scopeSearchResult?: IScopeSearchResult[] | null;
    searching?: boolean;
}

const NonStandardTemplateView = (props: IProps) => {
    // current view state/stages

    const [inputValue, setInputValue] = useState('');

    const [alertIsMounted, setAlertIsMounted] = useState(false);
    const [alertIsOpen, _setAlertIsOpen] = useState(false);
    const setAlertIsOpen: React.Dispatch<React.SetStateAction<boolean>> = (value) => {
        if (!alertIsMounted)
            setAlertIsMounted(true);
        _setAlertIsOpen(value);
    }

    const [sourceContext, setSourceContext] = useState<IImportNonstandardContext | null>({
        nonStandardSearchResult: null,
        scopeSearchResult: null,
        snapshot: props.state.data.snapshot,
        json: props.state.data.json,
    });

    const application = useApplication();

    const { instance } = useMsal();
    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);


    const categoryNonStandardTemplate = props.state.data.config.cache.map_bcbs_config_category_named['Non-Standard Template'];

    const dispatch = useDispatch();
    const history = useHistory();
    useEffect(() => {
        if (props.isOpen) {
            setSourceContext({
                snapshot: props.state.data.snapshot,
                json: props.state.data.json,
                scopeSearchResult: null,
            });
        }
    }, [props.isOpen, props.nonStandardTemplate]);



    const handleInputChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue: string | undefined) => {
        setInputValue(newValue ?? "");
    };

    /**
   * Search for all available non-standards inside a snapshot and output list of scopes
   * Group Configuration, Benefit 1, Benefit 2, ...
   * @param value 
   * @returns 
   */
    const onSearchScopes: TSearchPredicateAsync<INonstandardSearchResult> = async (value) => {
        const searchValue = value.toLowerCase() ?? '';

        if (!sourceContext?.snapshot || !sourceContext.json)
            throw new Error('expecting snapshot to search');

        return new Promise((resolve, reject) => {

            let json = sourceContext.json;

            let items: IGridItem<INonstandardSearchResult>[] = [];

            if (!json)
                reject(new Error('unexpected'));

            props.nonStandardTemplate?.relationships?.forEach((relationship, ix) => {
                let add = false;
                let relationshipExists = false;
                const scope = `Benefits`;// (${benefit.bcbs_name})/${benefit.name}`;
                const search = scope.toLowerCase();

                let categoryName = "";
                if (props.state.data.json.id === relationship.categoryID) {
                    categoryName = props.state.data.json.name;
                }
                else {
                    var benefit = props.state.data.json.benefits.find(ben => ben.id === relationship.categoryID);
                    if (benefit) categoryName = benefit.name;
                }

                 //if we are not searching for anything, return as/is
                if (!searchValue)
                    add = true;

                 //lets look into search
                if (scope && search.indexOf(searchValue) > -1)
                    add = true;

                if (add) {
                    var nonStandard: FAM.INonStandard | null = null;
                    var benefit: FAM.IBenefit | undefined = undefined;
                    relationship.sectionRefs?.forEach(section => {
                        let categoryId = relationship.categoryID;
                        section.nonstandards?.forEach(nonStandardRef => {
                            //find the non standard in the json   
                            let sectionId = section.sectionId;
                            section.nonstandards?.forEach(nonStandardRef => {
                                //find the benefits for the category id
                                benefit = props.state.data.json.benefits.find(ben => ben.id === categoryId);
                                if (benefit) {
                                    //do a find on the non standard
                                    let benefitNonStandard = benefit.nonStandards.find(ns => ns.id === nonStandardRef.id);

                                    //if we find the non standard, update the fields
                                    if (benefitNonStandard && benefitNonStandard.deactivated !== true) {
                                        nonStandard = benefitNonStandard;
                                    }
                                } else if (props.state.data.json.id === categoryId) {
                                    //if no benefits look at the GC
                                    let gcNonStandard = props.state.data.json.nonStandards.find(ns => ns.id === nonStandardRef.id);
                                    if (gcNonStandard && gcNonStandard.deactivated !== true) {
                                        nonStandard = gcNonStandard
                                    }
                                }
                            })

                            if (nonStandard) {
                                var sectionName = props.state.data.config.cache.map_bcbs_config_section[nonStandard.sectionRef!.id];
                                var tabName = props.state.data.config.cache.map_bcbs_config_tab[sectionName._bcbs_config_tabid_value!];
                                var configSection = props.state.data.config.cache.map_bcbs_config_section[sectionId];

                                const nsFields = ArrayToLookupByPredicateWithMap(nonStandard!.fields, t => t.configRef.id, t => {
                                    const attribute = props.state.data.config.cache.map_bcbs_config_attribute[t.configRef.id];
                                    const masterAttribute = props.state.data.config.cache.map_bcbs_config_masterattribute[attribute._bcbs_config_masterattributeid_value!];
                                    const controlType = masterAttribute.bcbs_attributetypecode as EControlType;
                                    return {
                                        field: t,
                                        attribute,
                                        masterAttribute,
                                        controlType,
                                    }
                                });
                                const effectiveDate = nsFields['0973f3f2-d38d-ec11-b400-0022480a9a56'];
                                let effectiveDateValue = "";

                                if (effectiveDate.field.values[0].value !== null) {
                                    effectiveDateValue = formatDate(parseDate(friendlyValue(effectiveDate.controlType, effectiveDate.field.values[0].value), 'local:yyyy-MM-dd'), 'en-US-ET');
                                }
                                

                                const item: IGridItem<INonstandardSearchResult> = {
                                    key: ix,
                                    _index: ix,
                                    name: nonStandardRef.name,
                                    scope: configSection.bcbs_name,
                                    tab: tabName.bcbs_name,
                                    effectiveDate: effectiveDateValue,
                                    category: benefit?.name ?? "Group Configuration",
                                    data: {
                                        name: nonStandardRef.name,
                                        id: nonStandardRef.id,
                                        item: nonStandard,
                                        benefit: benefit ?? undefined,
                                    }
                                };

                                items.push(item);
                            }

                        })
                    })
                }

            });

            const quickViewColumn: IColumn = {
                key: 'view',
                minWidth: 50,
                name: 'View',
                fieldName: 'view',
                isResizable: true,
                onRender(item: IGridItem<INonstandardSearchResult>, index, column) {
                    return onRenderInformationCallout(item)
                },
            };

            // generating a temporary non-standard to match the schema
            const columns = createNonStandardTemplateColumns(
                props.state.data.config,
                {
                    category: categoryNonStandardTemplate,
                    linkedCategory: null,
                    config: props.state.data.config,
                },
                undefined,
                undefined,
                (field, configAttribute, configMasterAttribute) => {

                    // https://bcbsonline.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=ff72f3f2-d38d-ec11-b400-0022480a9a56
                    if (configAttribute?.bcbs_config_attributeid === 'ff72f3f2-d38d-ec11-b400-0022480a9a56')
                        return true;

                    return false;
                }
            );

            resolve({
                columns: [
                    {
                        key: 'name',
                        minWidth: 350,
                        name: 'Name',
                        fieldName: 'name',
                        isResizable: true,
                    },
                    {
                        key: 'scope',
                        minWidth: 200,
                        name: 'Section',
                        fieldName: 'scope',
                        isResizable: true,
                    },
                    {
                        key: 'tab',
                        minWidth: 100,
                        name: 'Tab',
                        fieldName: 'tab',
                        isResizable: true,
                    },
                    {
                        key: 'category',
                        minWidth: 200,
                        name: 'Category',
                        fieldName: 'category',
                        isResizable: true,
                    },
                    {
                        key: 'effectiveDate',
                        minWidth: 150,
                        name: 'Effective Date',
                        fieldName: 'effectiveDate',
                        isResizable: true,
                    },
                    ...columns,
                    quickViewColumn,
                ],
                items: items,
                showNoResultPrompt: items.length === 0,
                showLimitedResultPrompt: false,

            })
        });
    }


    const onRenderInformationCallout = (auditData?: IGridItem<INonstandardSearchResult>) => {

        if (!auditData?.data?.item)
            return <></>;

        const nonStandard = auditData?.data?.item;

        const benefit = auditData?.data?.benefit
            ? auditData?.data?.benefit
            : undefined;

        return <InformationCallout
            hover
            postText={auditData.compareKeys}
            directionalHint={DirectionalHint.rightCenter}
            calloutClassName={appStyles.calloutView}
        >
            <div style={{ margin: '10px' }}>
                <NonStandardEditor
                    variant={EVariant.viewer}
                    isOpen={true}
                    defaultValue={nonStandard ?? null}
                    benefit={benefit}
                    disabled={true}
                />

            </div>
        </InformationCallout>;
    }

    const theme = useTheme();

    const columnProps: Partial<IStackProps> = useMemo(() => {
        return {
            tokens: { childrenGap: 15 },
            styles: {
                root: {
                    boxShadow: theme.effects.elevation16,
                    padding: '20px',
                    margin: '20px 0px 0px 0px',
                    backgroundColor: theme.semanticColors.menuBackground
                },
            },
        };
    }, [theme]);

    function onRenderBody(): JSX.Element {
        return <div className={fadeIn}>
            {onRenderValidationDialog()}
            <Stack tokens={{ childrenGap: 10 }}>

                {sourceContext?.searching &&
                    <PageLoading />
                }

                <LookupControlBase
                    label='Entity'
                    value={sourceContext?.scopeSearchResult?.[0]?.name}
                    disabled
                    onLookup={() => {
                        setSourceContext(q => {
                            return {
                                json: null,
                                nonStandardSearchResult: null,
                                scopeSearchResult: null,
                                snapshot: null
                            }
                        });
                    }}
                />

                <LookupControlDialog
                    isOpen={sourceContext?.snapshot !== null &&
                        sourceContext?.json !== null &&
                        sourceContext?.scopeSearchResult === null
                    }
                    label={'Non-Standard'}
                    onSearch={onSearchScopes}
                    variant={"single"}
                    onPick={(picks) => {

                        // nothing to do
                        if (!picks || picks.length < 1) {
                            setSourceContext(q => {
                                return {
                                    json: null,
                                    nonStandardTemplateSearchResult: null,
                                    scopeSearchResult: null,
                                    snapshot: null
                                }
                            });
                            return;
                        }

                        picks?.forEach(pick => {
                            //navigate to non Standard

                            let path = `/records/blueRelationship/${props.state.data.snapshot._bcbs_groupid_value}`;

                            path += `/groupConfiguration/${props.state.data.snapshot.bcbs_groupconfigurationsnapshotid}`;

                            if (pick.benefit)
                                path += `/benefit/${pick.benefit.id}`;

                            var section = props.state.data.config.cache.map_bcbs_config_section[pick!.item!.sectionRef!.id];
                            var tab = props.state.data.config.cache.map_bcbs_config_tab[section._bcbs_config_tabid_value!];
                            if (tab)
                                path += `/tab/${tab.bcbs_config_tabid}`;

                            if (section)
                                path += `/section/${section.bcbs_config_sectionid}`;

                            path += `/nonStandard/${pick.item.id}`;


                            dispatch(
                                WithRecycle(() => {
                                    history?.push({ pathname: path, search: '?navigation=pending' });
                                }, 100)
                            );

                        });
                    }}
                    onCancel={() => {

                        setSourceContext(q => {
                            return {
                                ...q,
                                snapshot: null,
                                nonStandardTemplateRelationshipSearchResult: null,
                                scopeSearchResult: null,
                                
                            }
                        });
                        props.onDismiss();
                    }}
                    onDismiss={() => {
                        setSourceContext(q => {
                            return {
                                ...q,
                                snapshot: null,
                                nonStandardTemplateRelationshipSearchResult: null,
                                scopeSearchResult: null,
                            }
                        });

                        props.onDismiss();
                    }}

                />
            </Stack>
        </div>
    }

    const onRenderValidationDialog = () => {
        if (!alertIsMounted)
            return <></>;

        return <ConfirmationDialog
            onContinue={() => {
                (async () => {
                    try {
                        dispatch(setProcessing(true));
                        const excelExtractImport = await import("global/ExcelExtract")
                        const { NonstandardExcelGeneration } = excelExtractImport;
                        const result = await NonstandardExcelGeneration(props.state, dataSource)
                        const fileName = `NSE-${props.state.data.snapshot?.bcbs_name}(${props.state.data.snapshot?.bcbs_groupconfigurationsnapshotid})-${new Date().getTime()}.xlsx`;
                        saveAs(result.file, fileName);
                    } catch (error) {
                        emitCustomEvent({
                            eventType: 'globalException',
                            details: error
                        });
                    } finally {
                        dispatch(setProcessing(false));
                    }
                })();
                setAlertIsOpen(false); setAlertIsMounted(false); props.onDismiss?.();
            }}
            onClose={() => { setAlertIsOpen(false); setAlertIsMounted(false); props.onDismiss?.(); }}
            continueText="Open NSE"
            cancelText="Close"
        >
            <Text>
            <p>Non-Standards have been updated or added as a result of this template action. </p>
            <p>You can open then NSE extract to view all Non-Standards and their changes.</p>
            </Text>
        </ConfirmationDialog>;
    }

    function matchingNonStandard(incomingNonStandard: FAM.INonStandard, incomingTemplate: FAM.INonStandardTemplate, categoryToCheck: FAM.ITemplateRelationship) {
        let returnIds: FAM.TGuid[] = [];
        const templatedNonStandard = structuredClone(incomingNonStandard);
        const template = structuredClone(incomingTemplate);
        const templateFieldMap = ArrayToLookupByPredicate(template.fields, q => q.configRef.id);
        let incomingNSCategoryId = categoryToCheck.categoryID;
        const nonStandardFields = ArrayToLookupByPredicateWithMap(templatedNonStandard!.fields, t => t.configRef.id, t => {
            const attribute = props.state.data.config.cache.map_bcbs_config_attribute[t.configRef.id];
            const masterAttribute = props.state.data.config.cache.map_bcbs_config_masterattribute[attribute._bcbs_config_masterattributeid_value!];
            const controlType = masterAttribute.bcbs_attributetypecode as EControlType;
            return {
                field: t,
                attribute,

                masterAttribute,
                controlType,
            }
        });
        const nonStandardDefinition = nonStandardFields['ff72f3f2-d38d-ec11-b400-0022480a9a56'];
        let nonStandardDefinitionValue = "";

        if (nonStandardDefinition.field.values[0].value !== null) {
            nonStandardDefinitionValue = friendlyValue(nonStandardDefinition.controlType, nonStandardDefinition.field.values[0].value);
        }

        let templateDefintion = templateFieldMap["1fe66c74-710c-ef11-9f89-6045bdeb64f8"];

        let templateDefinitionValue = friendlyValue(nonStandardDefinition.controlType, templateDefintion.values[0].value);
        let formattedTemplateDefinitionValue = templateDefinitionValue.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
        let formattedNonStandardDefinition = nonStandardDefinitionValue.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
        //take incoming category id

        //only find section refs of section ref on ns
        categoryToCheck.sectionRefs?.forEach(section => {
            if (section.sectionId === templatedNonStandard.sectionRef?.id) {
                let category: FAM.IBenefit | FAM.IGroupConfigurationData;
                //check group config id
                if (props.state.data.json.id === incomingNSCategoryId) {
                    category = props.state.data.json;
                    //go through each category non standard, make sure section ref is equal to sectionID, then compare
                    category.nonStandards.forEach(nonStandard => {
                        if (nonStandard.sectionRef?.id === section.sectionId && nonStandard.deactivated !== true) {
                            //we are in the section
                            //get non standard definition
                            const currentNonStandardFieldMap = ArrayToLookupByPredicate(nonStandard.fields, q => q.configRef.id);
                            const currentNonStandardDefinition = currentNonStandardFieldMap['ff72f3f2-d38d-ec11-b400-0022480a9a56'];
                            let currentNonStandardDefinitionValue = friendlyValue(nonStandardDefinition.controlType, currentNonStandardDefinition.values[0].value);
                            let formattedCurrentNonStandardDefinitionValue = currentNonStandardDefinitionValue.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

                            //compare they are the same
                            if (formattedNonStandardDefinition === formattedCurrentNonStandardDefinitionValue) {
                                //add id to list if they are the same
                                returnIds.push(nonStandard.id);
                            }
                        }
                    })
                }
                //check benefitID's
                let benefit = props.state.data.json.benefits.find(benefit => benefit.id === incomingNSCategoryId);
                if (benefit) {
                    category = benefit;
                    //go through each category non standard, make sure section ref is equal to sectionID, then compare
                    category.nonStandards.forEach(nonStandard => {
                        if (nonStandard.sectionRef?.id === section.sectionId && nonStandard.deactivated !== true) {
                            //we are in the section
                            //get non standard definition
                            const currentNonStandardFieldMap = ArrayToLookupByPredicate(nonStandard.fields, q => q.configRef.id);
                            const currentNonStandardDefinition = currentNonStandardFieldMap['ff72f3f2-d38d-ec11-b400-0022480a9a56'];
                            let currentNonStandardDefinitionValue = friendlyValue(nonStandardDefinition.controlType, currentNonStandardDefinition.values[0].value);
                            let formattedCurrentNonStandardDefinitionValue = currentNonStandardDefinitionValue.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

                            //compare they are the same
                            if (formattedNonStandardDefinition === formattedCurrentNonStandardDefinitionValue) {
                                //add id to list if they are the same
                                returnIds.push(nonStandard.id);
                            }
                        }
                    })
                }
            }
        })

        return returnIds;
    }

    function updateAllTemplatedNonStandardsFields(incomingTemplate: FAM.INonStandardTemplate) {
        //read template fields
        //clone the template
        const template = structuredClone(incomingTemplate);
        const templateFields = ArrayToLookupByPredicateWithMap(template!.fields, t => t.configRef.id, t => {
            const attribute = props.state.data.config.cache.map_bcbs_config_attribute[t.configRef.id];
            const masterAttribute = props.state.data.config.cache.map_bcbs_config_masterattribute[attribute._bcbs_config_masterattributeid_value!];
            const controlType = masterAttribute.bcbs_attributetypecode as EControlType;
            return {
                field: t,
                attribute,

                masterAttribute,
                controlType,
            }
        });
        const templateDefinition = templateFields['1fe66c74-710c-ef11-9f89-6045bdeb64f8'];
        const templatePayAccordingToBenefit = templateFields['05beb613-720c-ef11-9f89-6045bdeb64f8'];
        let templateDefinitionValue = "";
        let templatePayAccordingToBenefitValue = "";


        if (templateDefinition.field.values[0].value !== null) {
            templateDefinitionValue = friendlyValue(templateDefinition.controlType, templateDefinition.field.values[0].value);
        }
        if (templatePayAccordingToBenefit.field.values[0].value !== null) {
            templatePayAccordingToBenefitValue = friendlyValue(templatePayAccordingToBenefit.controlType, templatePayAccordingToBenefit.field.values[0].value);
        }

        //go through each connected NS and update the fields on that.
        template?.relationships?.forEach(rel => {
            let categoryId = rel.categoryID;
            rel.sectionRefs?.forEach(sectionRef => {
                let sectionId = sectionRef.sectionId;
                sectionRef.nonstandards?.forEach(nonStandardRef => {
                    //find the benefits for the category id
                    let benefit = props.state.data.json.benefits.find(ben => ben.id === categoryId);
                    if (benefit) {
                        //do a find on the non standard
                        let benefitNonStandard = benefit.nonStandards.find(ns => ns.id === nonStandardRef.id);

                        //if we find the non standard, update the fields
                        if (benefitNonStandard) {
                            //update fields
                            const updatedNonStandard: FAM.INonStandard = structuredClone(benefitNonStandard);
                            updatedNonStandard.partOfTemplate = true;
                            updatedNonStandard.name = template.name;

                            let nonStandardBenefitDefintion = updatedNonStandard.fields.find(field => field.configRef.id === "ff72f3f2-d38d-ec11-b400-0022480a9a56");
                            let nonStandardPayAccordingToBenefit = updatedNonStandard.fields.find(field => field.configRef.id === "fd72f3f2-d38d-ec11-b400-0022480a9a56");

                            const benefitDefinitionValue = templateDefinition.field.values[0].value; //template value
                            nonStandardBenefitDefintion!.values[0].value = structuredClone(benefitDefinitionValue); //ns value

                            if (nonStandardPayAccordingToBenefit) {
                                const payAccordingToBenefitValue = templatePayAccordingToBenefit.field.values[0].value; //template value
                                nonStandardPayAccordingToBenefit!.values[0].value = structuredClone(payAccordingToBenefitValue); //ns value
                            }

                            dispatch(addUpdateNonStandards([{
                                config: props.state.data.config,
                                nonStandards: [updatedNonStandard],
                                benefit: benefit,
                                //groupConfiguration: props.groupConfiguration,
                                changeTracker: {}
                            }]));
                        }
                    } else if (props.state.data.json.id === categoryId) {
                        //if no benefits look at the GC
                        let gcNonStandard = props.state.data.json.nonStandards.find(ns => ns.id === nonStandardRef.id);
                        if (gcNonStandard) {
                            //update fields
                            const updatedNonStandard: FAM.INonStandard = structuredClone(gcNonStandard);
                            updatedNonStandard.partOfTemplate = true;
                            updatedNonStandard.name = template.name;

                            let nonStandardBenefitDefintion = updatedNonStandard.fields.find(field => field.configRef.id === "ff72f3f2-d38d-ec11-b400-0022480a9a56");
                            let nonStandardPayAccordingToBenefit = updatedNonStandard.fields.find(field => field.configRef.id === "fd72f3f2-d38d-ec11-b400-0022480a9a56");

                            const benefitDefinitionValue = templateDefinition.field.values[0].value; //template value
                            nonStandardBenefitDefintion!.values[0].value = structuredClone(benefitDefinitionValue); //ns value

                            if (nonStandardPayAccordingToBenefit) {
                                const payAccordingToBenefitValue = templatePayAccordingToBenefit.field.values[0].value; //template value
                                nonStandardPayAccordingToBenefit!.values[0].value = structuredClone(payAccordingToBenefitValue); //ns value
                            }

                            dispatch(addUpdateNonStandards([{
                                config: props.state.data.config,
                                nonStandards: [updatedNonStandard],
                                benefit: benefit,
                                //groupConfiguration: props.groupConfiguration,
                                changeTracker: {}
                            }]));
                        }
                    }
                })
            })
        })
    }

    return onRenderBody()

}




function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(NonStandardTemplateView);



